import React, { useRef, useState, useEffect } from 'react';
import EmailEditor from 'react-email-editor';
import { Button, Space, Modal, message, Tabs, Switch, Typography, Divider } from 'antd';
import {
  EyeOutlined, CodeOutlined, SaveOutlined, UndoOutlined,
  RedoOutlined, DesktopOutlined, MobileOutlined,
  ThunderboltFilled, LayoutFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import CodeEditor from 'components/shared/CodeEditor';

const { Text, Title } = Typography;

const EnhancedHTMLEditor = ({ value, onChange, height = '550px' }) => {
  const emailEditorRef = useRef(null);
  const [editorMode, setEditorMode] = useState('visual');
  const [htmlCode, setHtmlCode] = useState(value || '');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (isLoaded && value && emailEditorRef.current) {
      loadDesignFromHTML(value);
    }
  }, [isLoaded, value]);

  const onEditorLoad = () => {
    setIsLoaded(true);
    if (value) loadDesignFromHTML(value);
    else loadDefaultTemplate();
  };

  const loadDesignFromHTML = (html) => {
    const design = {
      body: {
        rows: [{
          cells: [1],
          columns: [{
            contents: [{
              type: 'html',
              values: { html: html, containerPadding: '10px' }
            }]
          }]
        }]
      }
    };
    emailEditorRef.current?.editor?.loadDesign(design);
  };

  const loadDefaultTemplate = () => {
    const defaultDesign = {
      body: {
        rows: [
          {
            cells: [1],
            columns: [{
              contents: [{
                type: 'heading',
                values: {
                  text: 'Intelligence Briefing',
                  headingType: 'h1',
                  fontSize: '28px',
                  color: '#0a192f',
                  textAlign: 'center'
                }
              }]
            }]
          }
        ]
      }
    };
    emailEditorRef.current?.editor?.loadDesign(defaultDesign);
  };

  const exportHTML = () => {
    emailEditorRef.current?.editor?.exportHtml((data) => {
      const { html } = data;
      setHtmlCode(html);
      onChange?.(html);
      message.success('SOURCE EXPORTED');
    });
  };

  const handlePreview = () => {
    emailEditorRef.current?.editor?.exportHtml((data) => {
      const { html } = data;
      setHtmlCode(html);
      setPreviewVisible(true);
    });
  };

  const handleCodeChange = (newCode) => {
    setHtmlCode(newCode);
    onChange?.(newCode);
  };

  const switchToCodeEditor = () => {
    emailEditorRef.current?.editor?.exportHtml((data) => {
      const { html } = data;
      setHtmlCode(html);
      setEditorMode('code');
    });
  };

  const switchToVisualEditor = () => {
    if (htmlCode) loadDesignFromHTML(htmlCode);
    setEditorMode('visual');
  };

  return (
    <EditorWrapper>
      <ControlBar>
        <Space size="middle">
          <ModeSwitcher>
            <SwitchBtn $active={editorMode === 'visual'} onClick={switchToVisualEditor}>
              VISUAL
            </SwitchBtn>
            <SwitchBtn $active={editorMode === 'code'} onClick={switchToCodeEditor}>
              SOURCE
            </SwitchBtn>
          </ModeSwitcher>
        </Space>

        <Space size="middle">
          {editorMode === 'visual' && (
            <Space size={8}>
              <GhostAction icon={<UndoOutlined />} onClick={() => emailEditorRef.current?.editor?.undo()} />
              <GhostAction icon={<RedoOutlined />} onClick={() => emailEditorRef.current?.editor?.redo()} />
            </Space>
          )}
          <Divider type="vertical" style={{ height: 16 }} />
          <MinimalBtn icon={<EyeOutlined />} onClick={handlePreview}>PREVIEW</MinimalBtn>
          <CompactPrimary onClick={exportHTML}>SAVE HTML</CompactPrimary>
        </Space>
      </ControlBar>

      <CanvasArea>
        {editorMode === 'visual' ? (
          <EmailEditor
            ref={emailEditorRef}
            onLoad={onEditorLoad}
            minHeight={height}
            options={{
              displayMode: 'web',
              appearance: { theme: 'light', panels: { tools: { dock: 'left' } } },
              tools: {
                heading: { enabled: true },
                text: { enabled: true },
                image: { enabled: true },
                divider: { enabled: true },
                html: { enabled: true }
              }
            }}
          />
        ) : (
          <SourceContainer>
            <HelpBanner>
              <ThunderboltFilled style={{ color: 'var(--aura-navy)', fontSize: 14 }} />
              <Text style={{ fontSize: 10, fontWeight: 800, color: 'var(--aura-slate)', marginLeft: 8 }}>
                DIRECT STYLISTIC INJECTION ENABLED.
              </Text>
            </HelpBanner>
            <CodeEditor
              language="html"
              value={htmlCode}
              onChange={handleCodeChange}
              height={height}
            />
          </SourceContainer>
        )}
      </CanvasArea>

      <Modal
        title={
          <ModalTitle>
            <Text strong style={{ fontSize: 10, letterSpacing: 1, color: 'var(--aura-slate)' }}>FRAME PREVIEW</Text>
            <Divider type="vertical" style={{ height: 12, margin: '0 12px' }} />
            <Switch 
              size="small"
              checkedChildren={<MobileOutlined style={{ fontSize: 10 }} />} 
              unCheckedChildren={<DesktopOutlined style={{ fontSize: 10 }} />} 
              checked={previewDevice === 'mobile'}
              onChange={v => setPreviewDevice(v ? 'mobile' : 'desktop')}
            />
          </ModalTitle>
        }
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={previewDevice === 'mobile' ? 420 : 1000}
        footer={null}
        centered
      >
        <IframeWrapper $device={previewDevice}>
          <iframe srcDoc={htmlCode} title="Visual Frame" />
        </IframeWrapper>
      </Modal>
    </EditorWrapper>
  );
};

const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Inter', sans-serif;
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
`;

const ModeSwitcher = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
`;

const SwitchBtn = styled.button`
  height: 28px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? 'var(--aura-navy)' : 'var(--aura-slate)'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'};
`;

const CanvasArea = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
`;

const GhostAction = styled(Button)`
  border: none !important;
  background: transparent !important;
  color: var(--aura-slate) !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  &:hover { background: #f1f5f9 !important; color: var(--aura-navy) !important; }
`;

const MinimalBtn = styled(Button)`
  font-weight: 800 !important;
  font-size: 10px !important;
  height: 32px !important;
  border-radius: 6px !important;
  border-color: #f1f5f9 !important;
  color: var(--aura-slate) !important;
`;

const CompactPrimary = styled(Button)`
  font-weight: 850 !important;
  font-size: 10px !important;
  height: 32px !important;
  border-radius: 6px !important;
  background: var(--aura-navy) !important;
  color: white !important;
  border: none !important;
`;

const SourceContainer = styled.div`
  padding: 16px;
`;

const HelpBanner = styled.div`
  padding: 8px 16px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
`;

const IframeWrapper = styled.div`
  max-width: ${props => props.$device === 'mobile' ? '375px' : '100%'};
  margin: 0 auto;
  iframe {
    width: 100%;
    height: 600px;
    border: none;
    border-radius: 8px;
    background: white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  }
`;

export default EnhancedHTMLEditor;


